"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Modal = void 0;
var _react = _interopRequireWildcard(require("react"));
var _framerMotion = require("framer-motion");
var _modal = require("@cbhq/cds-common/animation/modal");
var _ModalParentContext = require("@cbhq/cds-common/overlays/ModalParentContext");
var _useA11yLabels2 = require("../../hooks/useA11yLabels");
var _layout = require("../../layout");
var _useMotionProps = require("../../motion/useMotionProps");
var _linaria = require("../../utils/linaria");
var _FocusTrap = require("../FocusTrap");
var _modalStyles = require("./modalStyles");
var _ModalWrapper = require("./ModalWrapper");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const Modal = /*#__PURE__*/(0, _react.memo)( /*#__PURE__*/(0, _react.forwardRef)((props, ref) => {
  const children = props.children,
    _props$visible = props.visible,
    visible = _props$visible === void 0 ? false : _props$visible,
    onRequestClose = props.onRequestClose,
    onDidClose = props.onDidClose,
    _props$disableOverlay = props.disableOverlayPress,
    disableOverlayPress = _props$disableOverlay === void 0 ? false : _props$disableOverlay,
    _props$disablePortal = props.disablePortal,
    disablePortal = _props$disablePortal === void 0 ? false : _props$disablePortal,
    disableFocusTrap = props.disableFocusTrap,
    accessibilityLabelledBy = props.accessibilityLabelledBy,
    accessibilityLabel = props.accessibilityLabel,
    customZIndex = props.zIndex,
    width = props.width,
    _props$dangerouslyDis = props.dangerouslyDisableResponsiveness,
    dangerouslyDisableResponsiveness = _props$dangerouslyDis === void 0 ? false : _props$dangerouslyDis,
    dangerouslySetPosition = props.dangerouslySetPosition,
    _props$shouldCloseOnE = props.shouldCloseOnEscPress,
    shouldCloseOnEscPress = _props$shouldCloseOnE === void 0 ? true : _props$shouldCloseOnE,
    className = props.className,
    id = props.id,
    testID = props.testID,
    role = props.role,
    hideCloseButton = props.hideCloseButton,
    hideDividers = props.hideDividers;
  const _useA11yLabels = (0, _useA11yLabels2.useA11yLabels)({
      accessibilityLabelledBy,
      accessibilityLabel
    }),
    labelledBySource = _useA11yLabels.labelledBySource,
    labelledBy = _useA11yLabels.labelledBy,
    label = _useA11yLabels.label;
  const motionProps = (0, _useMotionProps.useMotionProps)({
    enterConfigs: [_modal.animateInOpacityConfig, _modal.animateInScaleConfig],
    exitConfigs: [_modal.animateOutOpacityConfig, _modal.animateOutScaleConfig],
    exit: 'exit'
  });
  const handleClose = (0, _react.useCallback)(() => {
    onRequestClose === null || onRequestClose === void 0 ? void 0 : onRequestClose();
  }, [onRequestClose]);
  (0, _react.useImperativeHandle)(ref, () => ({
    onRequestClose: handleClose
  }), [handleClose]);
  const modalData = (0, _react.useMemo)(() => ({
    visible,
    onRequestClose: handleClose,
    accessibilityLabelledBy: labelledBySource,
    hideCloseButton,
    hideDividers
  }), [visible, handleClose, labelledBySource, hideCloseButton, hideDividers]);

  // TODO: remove render props as we no longer need the method to close modal
  const renderChildrenProps = (0, _react.useMemo)(() => ({
    closeModal: handleClose
  }), [handleClose]);
  const dialogStyles = (0, _react.useMemo)(() => {
    const styles = {};
    if (width) {
      styles.width = width;
    }
    if (dangerouslySetPosition) {
      styles.position = dangerouslySetPosition;
    }
    return styles;
  }, [dangerouslySetPosition, width]);
  return /*#__PURE__*/_react.default.createElement(_ModalWrapper.ModalWrapper, {
    accessibilityLabel: label,
    accessibilityLabelledBy: labelledBy,
    className: className,
    dangerouslyDisableResponsiveness: dangerouslyDisableResponsiveness,
    disableOverlayPress: disableOverlayPress,
    disablePortal: disablePortal,
    id: id,
    onDidClose: onDidClose,
    onOverlayPress: handleClose,
    role: role,
    testID: testID,
    visible: visible,
    zIndex: customZIndex
  }, /*#__PURE__*/_react.default.createElement(_framerMotion.m.div, _extends({}, motionProps, {
    className: (0, _linaria.cx)(_modalStyles.modalDefaultClassName, !dangerouslyDisableResponsiveness && _modalStyles.modalResponsiveClassName),
    "data-testid": "modal-dialog-motion",
    style: dialogStyles
  }), /*#__PURE__*/_react.default.createElement(_FocusTrap.FocusTrap, {
    disableFocusTrap: disableFocusTrap,
    onEscPress: shouldCloseOnEscPress ? handleClose : undefined
  }, /*#__PURE__*/_react.default.createElement(_layout.VStack, {
    background: "background",
    className: (0, _linaria.cx)(_modalStyles.modalDialogClassName, !dangerouslyDisableResponsiveness && _modalStyles.modalDialogResponsiveClassName),
    elevation: 2,
    overflow: "hidden",
    width: "100%"
  }, /*#__PURE__*/_react.default.createElement(_ModalParentContext.ModalParentContext.Provider, {
    value: modalData
  }, typeof children === 'function' ? children(renderChildrenProps) : children)))));
}));
exports.Modal = Modal;